import React, { createContext, useContext, useState } from "react";
import type { State, County } from "../../../shared/types/models/location";
import type { Freeze } from "../../../shared/types/models/freeze";
import type { Parcel } from "../../../shared/types/models/parcel";
import type { Document } from '../../../shared/types/models/document';
import type { Grantor } from '../../../shared/types/models/grantor';
import { useKeycloak } from './KeycloakContext';

interface UserContextType {
  state: State | null;
  setState: (s: State | null) => void;
  county: County | null;
  setCounty: (c: County | null) => void;
  freeze: Freeze | null;
  setFreeze: (p: Freeze | null) => void;
  parcels: Parcel[];
  setParcels: (p: Parcel[]) => void;
  document: Document | null;
  setDocument: (d: Document | null) => void;
  entities: Grantor[];
  setEntities: (e: Grantor[]) => void;
  addOrUpdateEntity: (e: Grantor) => void;
  refreshEntities: () => Promise<void>;
  grantors: Grantor[];
  setGrantors: (g: Grantor[]) => void;
  refreshGrantors: () => Promise<void>;
  apiBaseUrl: string;
  apiBaseUrlLoading: boolean;
  apiBaseUrlError: string | null;
  userPreferences: { defaultStateId?: number | null; defaultCountyId?: number | null; isTestMode?: boolean; hasLiveAccess?: boolean } | null;
  saveUserPreferences: (stateId: number | null, countyId: number | null, isTestMode?: boolean) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<State | null>(null);
  const [county, setCounty] = useState<County | null>(null);
  const [freeze, setFreeze] = useState<Freeze | null>(null);
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [document, setDocument] = useState<Document | null>(null);
  const [entities, setEntities] = useState<Grantor[]>([]);
  const [grantors, setGrantors] = useState<Grantor[]>([]);

  const [apiBaseUrl, setApiBaseUrl] = useState<string>("");
  const [apiBaseUrlLoading, setApiBaseUrlLoading] = useState<boolean>(true);
  const [apiBaseUrlError, setApiBaseUrlError] = useState<string | null>(null);
  const [userPreferences, setUserPreferences] = useState<{ defaultStateId?: number | null; defaultCountyId?: number | null; isTestMode?: boolean; hasLiveAccess?: boolean } | null>(null);

  const { getToken, initialized, keycloak } = useKeycloak();

  React.useEffect(() => {
    console.log('[UserContext] Loaded');
    setApiBaseUrlLoading(true);
    fetch("/config.json")
      .then(res => res.json())
      .then((config) => {
        setApiBaseUrl(config.API_BASE_URL || "");
        setApiBaseUrlLoading(false);
      })
      .catch((err) => {
        setApiBaseUrlError("Failed to load API base URL");
        setApiBaseUrlLoading(false);
      });
  }, []);

  React.useEffect(() => {
    const fetchPreferences = async () => {
      if (initialized && keycloak?.authenticated && apiBaseUrl) {
        try {
          const token = await getToken();
          if (!token) return;
          const res = await fetch(`${apiBaseUrl}/api/frozendeed/preferences`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setUserPreferences(data);
            if (data.isTestMode) {
              localStorage.setItem('identifid-mode', 'test');
            } else {
              localStorage.removeItem('identifid-mode');
            }
          }
        } catch (e) {
          console.warn('[UserContext] Failed to load preferences', e);
        }
      }
    };
    fetchPreferences();
  }, [initialized, keycloak?.authenticated, apiBaseUrl]);

  const saveUserPreferences = async (stateId: number | null, countyId: number | null, isTestMode?: boolean) => {
    try {
      const token = await getToken();
      if (!token || !apiBaseUrl) return;
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/preferences`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ defaultStateId: stateId, defaultCountyId: countyId, isTestMode })
      });
      if (res.ok) {
        const data = await res.json();
        setUserPreferences(data);
        
        // If the mode changed, reload the window to apply it to all fetches
        const currentLocalMode = localStorage.getItem('identifid-mode') === 'test';
        if (data.isTestMode !== currentLocalMode) {
          if (data.isTestMode) {
            localStorage.setItem('identifid-mode', 'test');
          } else {
            localStorage.removeItem('identifid-mode');
          }
          window.location.reload();
        }
      }
    } catch (e) {
      console.warn('[UserContext] Failed to save preferences', e);
    }
  };

  React.useEffect(() => {
    console.log('[UserContext] document changed:', document);
    // Auto-fetch entities when document changes and is not null
    const fetchEntities = async () => {
      if (document && document.lookup) {
        console.log('[UserContext] Fetching entities for document lookup:', document.lookup);
        const token = await getToken();
        if (!token) {
          console.warn('[UserContext] No token found from Keycloak for fetching entities');
          return;
        }
        const entities = await fetchEntitiesByDocumentLookup(document.lookup, token);
        console.log('[UserContext] About to setEntities, entities:', entities);
        setEntities(entities);
        console.log('[UserContext] Entities set after fetch:', entities);
      }
    };
    fetchEntities();
  }, [document]);

  React.useEffect(() => {
    console.log('[UserContext] entities changed:', entities);
  }, [entities]);

  React.useEffect(() => {
    const fetchGrantors = async () => {
      if (freeze && freeze.lookup && apiBaseUrl) {
        const token = await getToken();
        if (!token) return;
        try {
          const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/grantors`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
              setGrantors(data.data);
            }
          }
        } catch (e) {
          console.warn('[UserContext] Failed to fetch grantors', e);
        }
      }
    };
    fetchGrantors();
  }, [freeze]);

  const addOrUpdateEntity = (e: Grantor) => {
    // Normalize entity shape: always { id, documentId, metadata }
    let normalized: Grantor;
    if ('metadata' in e && e.metadata) {
      normalized = e;
    } else {
      // Extract id, documentId, type, and put the rest in metadata
      const { id, documentId, type, ...rest } = e as any;
      normalized = { id, documentId, type, metadata: rest } as Grantor;
    }
    setEntities(prev => {
      // Use lookup as the unique key for updating
      if ('lookup' in normalized) {
        const idx = prev.findIndex(x => (x as any).lookup === (normalized as any).lookup);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = normalized;
          return updated;
        }
      }
      return [...prev, normalized];
    });
  };

  // Add a helper to fetch entities by document lookup
  const fetchEntitiesByDocumentLookup = async (documentLookup: string, token: string) => {
    console.log('[UserContext] fetchEntitiesByDocumentLookup called with:', documentLookup);
    if (!apiBaseUrl) return [];
    const res = await fetch(`${apiBaseUrl}/api/frozendeed/entities?documentLookup=${documentLookup}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    console.log('[UserContext] fetchEntitiesByDocumentLookup result:', data);
    // Inject documentLookup into each entity
    return Array.isArray(data.data)
      ? data.data.map((entity: any) => ({ ...entity, documentLookup }))
      : [];
  };

  const refreshEntities = async () => {
    if (document && document.lookup) {
      const token = await getToken();
      if (!token) return;
      const data = await fetchEntitiesByDocumentLookup(document.lookup, token);
      setEntities(data);
    }
  };

  const refreshGrantors = async () => {
    if (freeze && freeze.lookup) {
      const token = await getToken();
      if (!token || !apiBaseUrl) return;
      try {
        const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/grantors`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.data)) {
            setGrantors(data.data);
          }
        }
      } catch (e) {
        console.warn('[UserContext] Failed to refresh grantors', e);
      }
    }
  };

  return (
    <UserContext.Provider value={{ 
      state, setState, county, setCounty, freeze, setFreeze, parcels, setParcels, document, setDocument, 
      entities, setEntities, addOrUpdateEntity, refreshEntities, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError,
      userPreferences, saveUserPreferences,
      grantors, setGrantors, refreshGrantors
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUserContext must be used within a UserProvider");
  return ctx;
}; 